import React from "react"
import { twMerge } from "tailwind-merge"

import placeholderImage from "../../../../assets/images/profile-photo-placeholder.jpg"
import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { getThemeShapeClasses } from "../utils/themeUtils"
import { buildServiceUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const ServicesSectionDesktop = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "list"
  const selectedServiceIds = settings.selectedServiceIds || []

  // Get theme colors with fallback generation
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    // If bgMedium, bgExtraLight, or bgLight are missing, generate them from primary color
    if (!colors.bgMedium || !colors.bgExtraLight || !colors.bgLight) {
      const primaryColor = colors.primary
      if (primaryColor) {
        const generatedPalette = generateColorPalette(primaryColor)
        colors.bgMedium = colors.bgMedium || generatedPalette.bgMedium
        colors.bgExtraLight = colors.bgExtraLight || generatedPalette.bgExtraLight
        colors.bgLight = colors.bgLight || generatedPalette.bgLight
      }
    }

    return colors
  }

  const themeColors = getThemeColors()

  // Resolve background color based on toggle setting
  const resolveSectionBackgroundColor = () => {
    // Check if background color toggle is enabled (handle both boolean and string values)
    const showBgColor = settings.showBackgroundColor === true || settings.showBackgroundColor === "true"
    const bgColorIsTheme = settings.backgroundColor === "theme"

    if (showBgColor && bgColorIsTheme) {
      // Use bgLight from theme colors
      return themeColors.bgLight || theme?.colors?.bgLight || "#FFFFFF"
    }
    // Default to white if toggle is off or not set
    return "#FFFFFF"
  }

  const sectionBackgroundColor = resolveSectionBackgroundColor()
  const headingColor = theme?.colors?.textDark || "#000000"
  const bodyColor = theme?.colors?.textDark || "#000000"

  const handleServiceClick = (serviceId) => {
    window.location.href = buildServiceUrl(serviceId, practice)
  }

  // Get practice services - filter to only show public services
  const allPracticeServices = (practice?.services || []).filter((service) => service.public === true)

  // If specific services are selected, use those in the specified order; otherwise use all services
  const selectedServices =
    selectedServiceIds.length > 0
      ? selectedServiceIds.map((id) => allPracticeServices.find((service) => service.id === id)).filter(Boolean)
      : allPracticeServices

  // Show all selected services (no limits)
  const allItems = selectedServices

  const formatPrice = (amountCents, currency) => {
    if (amountCents === 0) {
      return "Free"
    }
    const amount = amountCents / 100
    const currencySuffix = currency === "CAD" ? " CAD" : ""
    // If no cents (amount is a whole number), format without decimals
    if (amountCents % 100 === 0) {
      return `$${amount.toFixed(0)}${currencySuffix}`
    }
    return `$${amount.toFixed(2)}${currencySuffix}`
  }

  // Render service card for highlights layout (image-based design)
  const renderHighlightCard = (item, index) => {
    const price =
      typeof item.amountCents === "number"
        ? item.amountCents === 0
          ? "FREE"
          : formatPrice(item.amountCents, practice?.defaultCurrency || "USD")
        : ""
    const duration = item.timeLength ? `${item.timeLength} min` : ""

    // Format location and service type information for highlights
    const serviceType = item.virtual ? "Virtual" : "In-person"
    const location = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
    const locationType = item.atHome || item.at_home ? "At your home" : ""
    const locationInfo = [serviceType, location, locationType].filter(Boolean).join(" • ")

    // Get image URL from various possible sources
    const imageUrl =
      item.imageUrl ||
      item.mediumWebpUrl ||
      (item.photoHandles?.large?.webp ? `https://cdn.filestackcontent.com/${item.photoHandles.large.webp}` : null) ||
      (item.photoHandles?.original?.any ? `https://cdn.filestackcontent.com/${item.photoHandles.original.any}` : null)

    // Truncate description for display
    const displayDescription =
      item.description && item.description.length > 200 ? item.description.substring(0, 200) + "..." : item.description

    return (
      <div key={index} className="flex h-[600px] w-full flex-col items-start justify-start gap-[21px]">
        {/* Image (with placeholder fallback to preserve layout) */}
        <div
          className={twMerge(
            "aspect-[360/227] w-full min-w-[360px] max-w-[560px] overflow-hidden bg-gray-100",
            getThemeShapeClasses(theme)
          )}>
          <img
            src={imageUrl || placeholderImage}
            alt={item.name}
            className="h-full w-full object-cover"
            onError={(e) => {
              if (e.target.src !== placeholderImage) e.target.src = placeholderImage
            }}
          />
        </div>

        {/* Content - Fixed structure with consistent height - Left aligned */}
        <div className="flex w-full min-w-[360px] max-w-[560px] flex-1 flex-col items-start justify-start gap-2 text-left">
          {/* Price - First */}
          <span
            className="text-[20px] font-semibold"
            style={{
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              color: bodyColor
            }}>
            {price}
          </span>

          {/* Title - Second - Single line with ellipsis */}
          <h3
            className="w-full truncate text-[28px] font-bold leading-[40px]"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              color: headingColor
            }}>
            {item.name}
          </h3>

          {/* Duration and Location - Third */}
          {(duration || locationInfo) && (
            <p
              className="text-[16px] text-gray-600"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
              }}>
              {duration && locationInfo ? `${duration} • ${locationInfo}` : duration || locationInfo}
            </p>
          )}

          {/* Description - Fourth - Limited to 2 lines */}
          <div className="w-full">
            {displayDescription && (
              <div
                className="prose prose-lg line-clamp-2 w-full max-w-none text-[18px] leading-[32px]"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color: bodyColor,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden"
                }}
                dangerouslySetInnerHTML={{ __html: displayDescription }}></div>
            )}
          </div>

          {/* Details button - Close to content */}
          <div className="mt-4 flex w-full justify-start">
            <button
              onClick={() => handleServiceClick(item.id)}
              className="flex items-center justify-center gap-3 px-12 py-3 transition-opacity hover:opacity-90"
              style={{
                backgroundColor: theme?.colors?.primary || "#BD654F",
                borderRadius: theme?.borderRadius || "12px",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.bgLight || "#F4ECE9",
                fontSize: "16px",
                lineHeight: "24px"
              }}>
              Details
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Render service card for list layout (horizontal row design)
  const renderServiceCard = (item, index) => {
    const price =
      typeof item.amountCents === "number"
        ? item.amountCents === 0
          ? "FREE"
          : formatPrice(item.amountCents, practice?.defaultCurrency || "USD")
        : ""

    // Format duration
    const duration = item.timeLength ? `${item.timeLength} min` : ""

    // Format location and service type information
    const serviceType = item.virtual ? "Virtual" : "In-person"
    const location = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
    const locationType = item.atHome || item.at_home ? "At your home" : ""

    // Combine all location/service info with bullet points
    const locationInfo = [serviceType, location, locationType].filter(Boolean).join(" • ")

    // Get image URL from various possible sources
    const imageUrl =
      item.imageUrl ||
      item.mediumWebpUrl ||
      (item.photoHandles?.large?.webp ? `https://cdn.filestackcontent.com/${item.photoHandles.large.webp}` : null) ||
      (item.photoHandles?.original?.any ? `https://cdn.filestackcontent.com/${item.photoHandles.original.any}` : null)

    // Truncate description for display
    const displayDescription =
      item.description && item.description.length > 200 ? item.description.substring(0, 200) + "..." : item.description

    return (
      <div
        key={index}
        className={twMerge("relative w-full", getThemeShapeClasses(theme))}
        style={{ border: `1px solid ${theme?.colors?.stroke || "#E6E6E6"}` }}>
        <div className="relative flex w-full flex-row items-center justify-start gap-8 px-10 py-8">
          {/* Photo + Content */}
          <div className="relative flex min-h-px flex-1 flex-row items-center justify-start gap-8 p-0">
            {/* Large Square Image */}
            <div className={twMerge("h-48 w-48 shrink-0 overflow-hidden bg-gray-100", getThemeShapeClasses(theme))}>
              <img
                src={imageUrl || placeholderImage}
                alt={item.name}
                className="h-full w-full object-cover"
                onError={(e) => {
                  if (e.target.src !== placeholderImage) e.target.src = placeholderImage
                }}
              />
            </div>

            {/* Content */}
            <div className="relative flex min-h-px flex-1 flex-col items-start justify-start gap-2 overflow-hidden p-0 text-left">
              {/* Price + Title */}
              <div className="relative flex w-full flex-col items-start justify-start p-0">
                {price && (
                  <div
                    className="relative w-full truncate text-[20px]"
                    style={{
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                      fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                      color: bodyColor,
                      lineHeight: "32px"
                    }}>
                    {price}
                  </div>
                )}
                <div
                  className="relative line-clamp-2 w-full text-[28px]"
                  style={{
                    fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                    fontWeight: theme?.fonts?.headingWeight || "700",
                    color: headingColor,
                    lineHeight: "40px",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden"
                  }}>
                  {item.name}
                </div>
              </div>

              {/* Duration and Location */}
              {(duration || locationInfo) && (
                <div
                  className="relative w-full truncate text-[16px]"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                    color: bodyColor,
                    lineHeight: "24px"
                  }}>
                  {duration && locationInfo ? `${duration} • ${locationInfo}` : duration || locationInfo}
                </div>
              )}

              {/* Description */}
              {displayDescription && (
                <div
                  className="prose prose-lg relative line-clamp-2 w-full max-w-none text-[18px]"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                    color: bodyColor,
                    lineHeight: "32px",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden"
                  }}
                  dangerouslySetInnerHTML={{ __html: displayDescription }}
                />
              )}
            </div>
          </div>

          {/* Button */}
          <div
            className="relative inline-flex shrink-0 cursor-pointer flex-row items-center justify-center gap-3 px-12 py-6 transition-opacity hover:opacity-90"
            style={{
              backgroundColor: theme?.colors?.primary || "#bd654f",
              borderRadius: theme?.borderRadius || "12px"
            }}
            onClick={() => handleServiceClick(item.id)}>
            <div
              className="relative flex shrink-0 flex-col justify-center text-nowrap text-left text-[16px] leading-[0]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.bgLight || "#f4ece9",
                lineHeight: "24px"
              }}>
              Details
            </div>
          </div>
        </div>
      </div>
    )
  }

  const getGridClasses = () => {
    switch (layout) {
      case "highlights_2":
        return "grid grid-cols-2 gap-10 items-stretch" // 2 columns with equal height
      case "highlights_3":
        return "grid grid-cols-3 gap-10 items-stretch" // 3 columns with equal height, wraps to next row automatically
      case "list":
      default:
        return "flex flex-col gap-6" // Vertical stack for list layout - matches Figma gap-6
    }
  }

  const isHighlightLayout = layout === "highlights_2" || layout === "highlights_3"

  return (
    <section
      className={twMerge("relative pb-[72px] pt-14", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{
        backgroundColor: sectionBackgroundColor
      }}>
      <AnimatedSection className="mx-auto max-w-[1160px] px-0">
        <div className="flex w-full flex-col items-start gap-16">
          {/* Header */}
          <div className="flex w-full flex-col items-start gap-8">
            {/* Title and Browse Services Button */}
            <div className="flex w-full items-center justify-between gap-10">
              <div className="flex flex-col items-start gap-4">
                <h2
                  className="text-left text-[40px] font-bold leading-[48px]"
                  style={{
                    fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                    fontWeight: theme?.fonts?.headingWeight || "700",
                    color: headingColor
                  }}>
                  {section.headline || "Services"}
                </h2>
                {section.body && (
                  <p
                    className="max-w-3xl text-left text-xl leading-relaxed"
                    style={{
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                      fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                      color: bodyColor
                    }}>
                    {section.body}
                  </p>
                )}
              </div>

              {/* Browse Services Button - Not shown on services section (this is the services page) */}
            </div>
          </div>

          {/* Services Grid */}
          {allItems.length > 0 && (
            <div className="w-full">
              {isHighlightLayout ? (
                // Highlights layout - grid or flex layout
                <div className={getGridClasses()}>
                  {allItems.map((item, index) => renderHighlightCard(item, index))}
                </div>
              ) : (
                // List layout - vertical stack
                <div className={getGridClasses()}>{allItems.map((item, index) => renderServiceCard(item, index))}</div>
              )}
            </div>
          )}

          {/* Empty State */}
          {allItems.length === 0 && (
            <div className="flex flex-col items-center gap-6 py-16 text-center">
              <div className="text-6xl">🔧</div>
              <h3
                className="text-2xl font-semibold"
                style={{
                  fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                  color: headingColor
                }}>
                No services available
              </h3>
              <p
                className="text-lg"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color: bodyColor
                }}>
                {practice?.services?.length === 0
                  ? "This practice hasn't added any services yet."
                  : "No services match the current selection."}
              </p>
            </div>
          )}

          {/* Show More Button - Not needed for Services section (shows all services) */}
        </div>
      </AnimatedSection>
    </section>
  )
}

export default ServicesSectionDesktop
